/**
 * ZoomableTimeline — Phase 1 + Phase 2 + Smooth Replay
 * TL-05/TL-08/TL-09/TL-10/TL-11/TL-12/TL-13/TL-14/TL-15/TL-16/TL-17/TL-27
 *
 * Features:
 * - Multi-level zoom: Day (00h→24h) → Hour (15min blocks) → 15min (minute by minute)
 * - Colored segments by activity status
 * - Draggable cursor synchronized with map
 * - Play/Pause with speed controls (x1, x2, x4)
 * - DATA-DRIVEN playback: advances through actual GPS timestamps
 * - Smart gap handling: gaps >30s get compressed
 * - Continuous interpolation between GPS points
 * - Back button to return to upper zoom level
 * - Dynamic popup (time, speed, coordinates)
 * - Snapped data indicator
 */
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Play, Pause, SkipBack, SkipForward, ZoomOut, ZoomIn, RotateCcw, FastForward } from 'lucide-react';

const ACTIVITY_COLORS = {
  ONLINE: '#10B981',
  MISSION: '#3B82F6',
  OFFLINE: '#6B7280',
  ARRET: '#F59E0B',
  STOP: '#F59E0B',
};

const ACTIVITY_LABELS = {
  ONLINE: 'En ligne',
  MISSION: 'Mission',
  OFFLINE: 'Hors ligne',
  ARRET: 'Arrêt',
  STOP: 'Arrêt',
};

// Zoom levels
const ZOOM_DAY = 0;    // 00h → 24h, labels every 2h
const ZOOM_HOUR = 1;   // 1h range, labels every 15min
const ZOOM_QUARTER = 2; // 15min range, labels every minute

// Gap threshold for smart compression (ms)
const GAP_COMPRESS_MS = 30000; // 30s — gaps > this get compressed
const GAP_DISPLAY_MS = 500;    // Compressed gaps display as 500ms in replay

function interpolatePosition(pointA, pointB, ratio) {
  if (!pointA || !pointB) return pointA || pointB || null;
  const clamp = Math.max(0, Math.min(1, ratio));
  return {
    lat: pointA.lat + (pointB.lat - pointA.lat) * clamp,
    lng: pointA.lng + (pointB.lng - pointA.lng) * clamp,
    speed: (pointA.speed || 0) + ((pointB.speed || 0) - (pointA.speed || 0)) * clamp,
  };
}

function toMs(ts) {
  if (!ts) return 0;
  return new Date(ts).getTime();
}

function fmtTime(ms) {
  const d = new Date(ms);
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fmtTimeShort(ms) {
  const d = new Date(ms);
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function fmtDate(ms) {
  const d = new Date(ms);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

/** Generate axis labels based on zoom level and range */
function generateLabels(zoomLevel, rangeStart, rangeEnd) {
  const labels = [];
  const duration = rangeEnd - rangeStart;

  if (zoomLevel === ZOOM_DAY) {
    const baseDate = new Date(rangeStart);
    baseDate.setMinutes(0, 0, 0);
    for (let h = 0; h <= 23; h += 2) {
      const t = new Date(baseDate); t.setHours(h);
      const ms = t.getTime();
      if (ms >= rangeStart && ms <= rangeEnd) {
        labels.push({ ms, label: `${String(h).padStart(2, '0')}h`, ratio: (ms - rangeStart) / duration });
      }
    }
  } else if (zoomLevel === ZOOM_HOUR) {
    const step = 15 * 60 * 1000;
    let t = Math.ceil(rangeStart / step) * step;
    while (t <= rangeEnd) {
      labels.push({ ms: t, label: fmtTimeShort(t), ratio: (t - rangeStart) / duration });
      t += step;
    }
  } else if (zoomLevel === ZOOM_QUARTER) {
    const step = 60 * 1000;
    let t = Math.ceil(rangeStart / step) * step;
    while (t <= rangeEnd) {
      const d = new Date(t);
      labels.push({ ms: t, label: `${String(d.getMinutes()).padStart(2, '0')}`, ratio: (t - rangeStart) / duration });
      t += step;
    }
  }
  return labels;
}

/** Get zoom range label */
function getZoomLabel(zoomLevel, rangeStart, rangeEnd) {
  if (zoomLevel === ZOOM_DAY) return fmtDate(rangeStart);
  if (zoomLevel === ZOOM_HOUR) return `${fmtTimeShort(rangeStart)} — ${fmtTimeShort(rangeEnd)}`;
  return `${fmtTimeShort(rangeStart)} — ${fmtTimeShort(rangeEnd)}`;
}

// Event type icons/colors for TL-22
const EVENT_STYLES = {
  MISSION_ACCEPTEE: { color: '#3B82F6', label: 'Mission acceptée', icon: '●' },
  EN_APPROCHE: { color: '#8B5CF6', label: 'En approche', icon: '▶' },
  PRISE_EN_CHARGE_CLIENT: { color: '#10B981', label: 'Prise en charge', icon: '▲' },
  DEPOSE_CLIENT: { color: '#EF4444', label: 'Dépose client', icon: '▼' },
  ARRET: { color: '#F59E0B', label: 'Arrêt', icon: '■' },
  FIN_DE_MISSION: { color: '#6B7280', label: 'Fin mission', icon: '○' },
};

// Speed options: x1 = real-time, x2 = 2x, x4 = 4x
const SPEED_OPTIONS = [1, 2, 4];

export default function ZoomableTimeline({ points, segments, events, onPositionChange, onEventClick, onZoomLevelChange, dateStr, isSnapped }) {
  const [zoomLevel, setZoomLevel] = useState(ZOOM_DAY);
  const [zoomRangeOverride, setZoomRangeOverride] = useState(null);
  const [cursorRatio, setCursorRatio] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [hoverRatio, setHoverRatio] = useState(null);
  const [activeEvent, setActiveEvent] = useState(null);
  const barRef = useRef(null);
  const animRef = useRef(null);
  const lastFrameRef = useRef(null);

  // Base day range
  const dayRange = useMemo(() => {
    const base = dateStr || new Date().toISOString().slice(0, 10);
    return {
      start: new Date(`${base}T00:00:00`).getTime(),
      end: new Date(`${base}T23:59:59`).getTime(),
    };
  }, [dateStr]);

  // Active time range (based on zoom level)
  const timeRange = useMemo(() => {
    if (zoomRangeOverride) return zoomRangeOverride;
    return dayRange;
  }, [dayRange, zoomRangeOverride]);

  const pointTimestamps = useMemo(() => {
    if (!points?.length) return [];
    return points.map(p => toMs(p.recorded_at));
  }, [points]);

  const dataRange = useMemo(() => {
    if (!pointTimestamps.length) return null;
    return { start: pointTimestamps[0], end: pointTimestamps[pointTimestamps.length - 1] };
  }, [pointTimestamps]);

  // Build a compressed timeline for smooth replay
  // Maps real GPS timestamps to a compressed virtual timeline that skips long gaps
  const compressedTimeline = useMemo(() => {
    if (!pointTimestamps.length || pointTimestamps.length < 2) return null;

    let virtualTime = 0;
    const mapping = [{ realMs: pointTimestamps[0], virtualMs: 0 }];

    for (let i = 1; i < pointTimestamps.length; i++) {
      const dt = pointTimestamps[i] - pointTimestamps[i - 1];
      if (dt > GAP_COMPRESS_MS) {
        // Compress long gaps to GAP_DISPLAY_MS
        virtualTime += GAP_DISPLAY_MS;
      } else {
        virtualTime += dt;
      }
      mapping.push({ realMs: pointTimestamps[i], virtualMs: virtualTime });
    }

    return {
      mapping,
      totalVirtualMs: virtualTime,
    };
  }, [pointTimestamps]);

  const currentTimestamp = useMemo(() => {
    return timeRange.start + cursorRatio * (timeRange.end - timeRange.start);
  }, [cursorRatio, timeRange]);

  // Find interpolated position using binary search + lerp
  const findPosition = useCallback((timestamp) => {
    if (!points?.length || !pointTimestamps.length) return null;
    if (timestamp <= pointTimestamps[0]) return { ...points[0], interpolated: false };
    if (timestamp >= pointTimestamps[pointTimestamps.length - 1]) return { ...points[points.length - 1], interpolated: false };

    // Binary search for the bracketing segment
    let lo = 0, hi = pointTimestamps.length - 1;
    while (lo < hi - 1) {
      const mid = Math.floor((lo + hi) / 2);
      if (pointTimestamps[mid] <= timestamp) lo = mid;
      else hi = mid;
    }
    const t1 = pointTimestamps[lo], t2 = pointTimestamps[hi];
    const ratio = t2 > t1 ? (timestamp - t1) / (t2 - t1) : 0;
    return { ...interpolatePosition(points[lo], points[hi], ratio), interpolated: true, pointIndex: lo };
  }, [points, pointTimestamps]);

  // Convert virtual (compressed) time to real timestamp
  const virtualToReal = useCallback((virtualMs) => {
    if (!compressedTimeline) return timeRange.start;
    const { mapping } = compressedTimeline;
    if (virtualMs <= 0) return mapping[0].realMs;
    if (virtualMs >= compressedTimeline.totalVirtualMs) return mapping[mapping.length - 1].realMs;

    // Binary search in compressed mapping
    let lo = 0, hi = mapping.length - 1;
    while (lo < hi - 1) {
      const mid = Math.floor((lo + hi) / 2);
      if (mapping[mid].virtualMs <= virtualMs) lo = mid;
      else hi = mid;
    }

    const v1 = mapping[lo].virtualMs, v2 = mapping[hi].virtualMs;
    const r1 = mapping[lo].realMs, r2 = mapping[hi].realMs;
    const ratio = v2 > v1 ? (virtualMs - v1) / (v2 - v1) : 0;
    return r1 + ratio * (r2 - r1);
  }, [compressedTimeline, timeRange]);

  // Convert real timestamp to virtual (compressed) time
  const realToVirtual = useCallback((realMs) => {
    if (!compressedTimeline) return 0;
    const { mapping } = compressedTimeline;
    if (realMs <= mapping[0].realMs) return 0;
    if (realMs >= mapping[mapping.length - 1].realMs) return compressedTimeline.totalVirtualMs;

    let lo = 0, hi = mapping.length - 1;
    while (lo < hi - 1) {
      const mid = Math.floor((lo + hi) / 2);
      if (mapping[mid].realMs <= realMs) lo = mid;
      else hi = mid;
    }

    const r1 = mapping[lo].realMs, r2 = mapping[hi].realMs;
    const v1 = mapping[lo].virtualMs, v2 = mapping[hi].virtualMs;
    const ratio = r2 > r1 ? (realMs - r1) / (r2 - r1) : 0;
    return v1 + ratio * (v2 - v1);
  }, [compressedTimeline]);

  // Emit position
  const onPositionChangeRef = useRef(onPositionChange);
  onPositionChangeRef.current = onPositionChange;
  const onZoomLevelChangeRef = useRef(onZoomLevelChange);
  onZoomLevelChangeRef.current = onZoomLevelChange;

  // Emit zoom level changes
  useEffect(() => {
    onZoomLevelChangeRef.current?.(zoomLevel);
  }, [zoomLevel]);

  useEffect(() => {
    const pos = findPosition(currentTimestamp);
    if (pos) {
      onPositionChangeRef.current?.({
        lat: pos.lat, lng: pos.lng,
        speed: pos.speed || 0,
        timestamp: currentTimestamp,
        timeStr: fmtTime(currentTimestamp),
      });
    }
  }, [currentTimestamp, findPosition]);

  // DATA-DRIVEN playback animation with gap compression
  // Instead of advancing the cursor ratio uniformly through the view range,
  // we advance through the compressed virtual timeline, then map back to real time.
  useEffect(() => {
    if (!isPlaying) {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      lastFrameRef.current = null;
      return;
    }

    const animate = (now) => {
      if (lastFrameRef.current && compressedTimeline) {
        const frameDt = now - lastFrameRef.current; // real ms since last frame

        // Convert current cursor position to virtual time
        const currentRealMs = timeRange.start + cursorRatio * (timeRange.end - timeRange.start);
        const currentVirtual = realToVirtual(currentRealMs);

        // Advance virtual time by frameDt * playSpeed
        const newVirtual = currentVirtual + frameDt * playSpeed;

        if (newVirtual >= compressedTimeline.totalVirtualMs) {
          // Reached end of data
          const endRealMs = compressedTimeline.mapping[compressedTimeline.mapping.length - 1].realMs;
          const endRatio = (endRealMs - timeRange.start) / (timeRange.end - timeRange.start);
          setCursorRatio(Math.min(1, endRatio));
          setIsPlaying(false);
          lastFrameRef.current = null;
          return;
        }

        // Convert back to real timestamp
        const newRealMs = virtualToReal(newVirtual);
        const newRatio = (newRealMs - timeRange.start) / (timeRange.end - timeRange.start);
        setCursorRatio(Math.max(0, Math.min(1, newRatio)));
      } else if (lastFrameRef.current && !compressedTimeline) {
        // Fallback: uniform advancement when no data for compression
        const dt = now - lastFrameRef.current;
        const totalDuration = timeRange.end - timeRange.start;
        const advanceRatio = (dt * playSpeed * 60) / totalDuration;
        setCursorRatio(prev => {
          const next = prev + advanceRatio;
          if (next >= 1) { setIsPlaying(false); return 1; }
          return next;
        });
      }
      lastFrameRef.current = now;
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [isPlaying, playSpeed, timeRange, compressedTimeline, virtualToReal, realToVirtual, cursorRatio]);

  // Drag handling
  const handleBarInteraction = useCallback((clientX) => {
    if (!barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setCursorRatio(ratio);
  }, []);

  const dragStartRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setIsPlaying(false);
    dragStartRef.current = { x: e.clientX, moved: false };
    handleBarInteraction(e.clientX);
  }, [handleBarInteraction]);

  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e) => {
      if (dragStartRef.current) {
        const dx = Math.abs(e.clientX - dragStartRef.current.x);
        if (dx > 3) dragStartRef.current.moved = true;
      }
      handleBarInteraction(e.clientX);
    };
    const handleUp = (e) => {
      setIsDragging(false);
      if (dragStartRef.current && !dragStartRef.current.moved && barRef.current) {
        const rect = barRef.current.getBoundingClientRect();
        const clickRatio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const clickTs = timeRange.start + clickRatio * (timeRange.end - timeRange.start);
        handleZoomIn(clickTs);
      }
      dragStartRef.current = null;
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
  }, [isDragging, handleBarInteraction, timeRange]);

  // Hover for tooltip
  const handleBarHover = useCallback((e) => {
    if (!barRef.current || isDragging) return;
    const rect = barRef.current.getBoundingClientRect();
    setHoverRatio(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)));
  }, [isDragging]);

  const hoverTimestamp = useMemo(() => {
    if (hoverRatio === null) return null;
    return timeRange.start + hoverRatio * (timeRange.end - timeRange.start);
  }, [hoverRatio, timeRange]);

  // TL-09/TL-10: Zoom in on click
  const handleZoomIn = useCallback((clickTs) => {
    if (zoomLevel >= ZOOM_QUARTER) return;

    let newStart, newEnd, newLevel;
    if (zoomLevel === ZOOM_DAY) {
      const d = new Date(clickTs);
      d.setMinutes(0, 0, 0);
      newStart = d.getTime();
      newEnd = newStart + 3600 * 1000 - 1;
      newLevel = ZOOM_HOUR;
    } else if (zoomLevel === ZOOM_HOUR) {
      const d = new Date(clickTs);
      const quarter = Math.floor(d.getMinutes() / 15) * 15;
      d.setMinutes(quarter, 0, 0);
      newStart = d.getTime();
      newEnd = newStart + 15 * 60 * 1000 - 1;
      newLevel = ZOOM_QUARTER;
    }

    if (newStart !== undefined) {
      const cursorTs = currentTimestamp;
      const newRatio = Math.max(0, Math.min(1, (cursorTs - newStart) / (newEnd - newStart)));
      setZoomRangeOverride({ start: newStart, end: newEnd });
      setZoomLevel(newLevel);
      setCursorRatio(newRatio);
    }
  }, [zoomLevel, currentTimestamp]);

  // TL-11: Zoom out
  const handleZoomOut = useCallback(() => {
    if (zoomLevel === ZOOM_DAY) return;

    const cursorTs = currentTimestamp;
    let newStart, newEnd, newLevel;

    if (zoomLevel === ZOOM_QUARTER) {
      const d = new Date(cursorTs);
      d.setMinutes(0, 0, 0);
      newStart = d.getTime();
      newEnd = newStart + 3600 * 1000 - 1;
      newLevel = ZOOM_HOUR;
      setZoomRangeOverride({ start: newStart, end: newEnd });
    } else if (zoomLevel === ZOOM_HOUR) {
      newLevel = ZOOM_DAY;
      newStart = dayRange.start;
      newEnd = dayRange.end;
      setZoomRangeOverride(null);
    }

    setZoomLevel(newLevel);
    const newRatio = Math.max(0, Math.min(1, (cursorTs - newStart) / (newEnd - newStart)));
    setCursorRatio(newRatio);
  }, [zoomLevel, currentTimestamp, dayRange]);

  const skipBack = () => setCursorRatio(prev => Math.max(0, prev - 0.01));
  const skipForward = () => setCursorRatio(prev => Math.min(1, prev + 0.01));
  const cycleSpeed = () => setPlaySpeed(prev => {
    const idx = SPEED_OPTIONS.indexOf(prev);
    return SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length];
  });

  // Replay: jump to first GPS data point and auto-play
  const handleReplay = useCallback(() => {
    if (!dataRange) return;
    const ratio = Math.max(0, Math.min(1, (dataRange.start - timeRange.start) / (timeRange.end - timeRange.start)));
    setCursorRatio(ratio);
    setPlaySpeed(1);
    setIsPlaying(true);
  }, [dataRange, timeRange]);

  // Generate labels for current zoom level
  const axisLabels = useMemo(() => generateLabels(zoomLevel, timeRange.start, timeRange.end), [zoomLevel, timeRange]);

  // Build colored segment bars within current zoom range
  const segmentBars = useMemo(() => {
    if (!segments?.length) return [];
    const duration = timeRange.end - timeRange.start;
    if (duration <= 0) return [];

    return segments.map((seg, i) => {
      const segStart = toMs(seg.start);
      const segEnd = seg.end ? toMs(seg.end) : (segments[i + 1] ? toMs(segments[i + 1].start) : dayRange.end);

      const visStart = Math.max(segStart, timeRange.start);
      const visEnd = Math.min(segEnd, timeRange.end);
      if (visEnd <= visStart) return null;

      const left = (visStart - timeRange.start) / duration;
      const width = (visEnd - visStart) / duration;
      return {
        left: `${left * 100}%`,
        width: `${Math.max(width, 0.002) * 100}%`,
        color: ACTIVITY_COLORS[seg.activity] || '#6B7280',
        activity: seg.activity,
        label: ACTIVITY_LABELS[seg.activity] || seg.activity,
        timeLabel: fmtTimeShort(Math.max(segStart, timeRange.start)),
      };
    }).filter(Boolean);
  }, [segments, timeRange, dayRange]);

  // Current activity at cursor
  const currentActivity = useMemo(() => {
    if (!segments?.length) return null;
    for (let i = segments.length - 1; i >= 0; i--) {
      if (currentTimestamp >= toMs(segments[i].start)) return segments[i];
    }
    return segments[0];
  }, [segments, currentTimestamp]);

  // TL-22: Event markers on the timeline bar
  const eventMarkers = useMemo(() => {
    if (!events?.length) return [];
    const duration = timeRange.end - timeRange.start;
    if (duration <= 0) return [];
    return events.map((evt, i) => {
      const evtMs = toMs(evt.timestamp);
      if (evtMs < timeRange.start || evtMs > timeRange.end) return null;
      const ratio = (evtMs - timeRange.start) / duration;
      const style = EVENT_STYLES[evt.event_type] || { color: '#9CA3AF', label: evt.event_type, icon: '●' };
      return { ...evt, ratio, style, index: i };
    }).filter(Boolean);
  }, [events, timeRange]);

  // Hover tooltip data
  const hoverPos = useMemo(() => {
    if (hoverTimestamp === null) return null;
    return findPosition(hoverTimestamp);
  }, [hoverTimestamp, findPosition]);

  const pos = findPosition(currentTimestamp);
  const hasData = points?.length > 0;
  const zoomLabel = getZoomLabel(zoomLevel, timeRange.start, timeRange.end);
  const zoomLevelNames = ['Jour', 'Heure', '15 min'];

  return (
    <div className="bg-[#0e0e1a] rounded-lg border border-white/10 select-none" data-testid="zoomable-timeline">
      {/* Controls row */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/5">
        {/* Zoom controls */}
        <div className="flex items-center gap-0.5">
          <button onClick={handleZoomOut} disabled={zoomLevel === ZOOM_DAY}
            className={`p-1 rounded transition-colors ${zoomLevel > ZOOM_DAY ? 'text-white/50 hover:bg-white/10 hover:text-white' : 'text-white/15 cursor-not-allowed'}`}
            data-testid="timeline-zoom-out" title="Zoom arrière">
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <span className="text-[9px] text-white/30 font-medium px-1 min-w-[32px] text-center" data-testid="timeline-zoom-level">
            {zoomLevelNames[zoomLevel]}
          </span>
          <button onClick={() => handleZoomIn(currentTimestamp)} disabled={zoomLevel >= ZOOM_QUARTER}
            className={`p-1 rounded transition-colors ${zoomLevel < ZOOM_QUARTER ? 'text-white/50 hover:bg-white/10 hover:text-white' : 'text-white/15 cursor-not-allowed'}`}
            data-testid="timeline-zoom-in" title="Zoom avant">
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="w-px h-4 bg-white/10" />

        {/* Play controls */}
        <div className="flex items-center gap-0.5">
          {/* Replay from start */}
          <button onClick={handleReplay} disabled={!hasData}
            className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium transition-all ${hasData ? 'bg-[#C9A24D]/10 text-[#C9A24D] hover:bg-[#C9A24D]/25' : 'text-white/15 cursor-not-allowed'}`}
            data-testid="timeline-replay-btn" title="Rejouer depuis le premier point GPS">
            <RotateCcw className="w-3 h-3" />
            <span>Replay</span>
          </button>
          <button onClick={skipBack} className="p-1 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
            data-testid="timeline-skip-back" title="Reculer">
            <SkipBack className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setIsPlaying(!isPlaying)}
            className={`p-1.5 rounded-full transition-all ${isPlaying ? 'bg-[#C9A24D] text-[#0B0B0B]' : 'bg-[#C9A24D]/20 text-[#C9A24D] hover:bg-[#C9A24D]/30'}`}
            data-testid="timeline-play-btn" title={isPlaying ? 'Pause' : 'Lecture'}>
            {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          </button>
          <button onClick={skipForward} className="p-1 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
            data-testid="timeline-skip-forward" title="Avancer">
            <SkipForward className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Speed control — cycle through x1/x2/x4 */}
        <button onClick={cycleSpeed}
          className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold transition-colors ${playSpeed >= 4 ? 'bg-[#C9A24D]/15 text-[#C9A24D] hover:bg-[#C9A24D]/25' : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'}`}
          data-testid="timeline-speed-btn" title="Vitesse de lecture (cliquer pour changer)">
          <FastForward className="w-3 h-3" />x{playSpeed}
        </button>

        {/* Current time + speed */}
        <div className="flex-1 text-center">
          <span className="text-[11px] font-mono text-[#C9A24D] font-medium" data-testid="timeline-current-time">
            {fmtTime(currentTimestamp)}
          </span>
          {pos && (
            <span className="text-[10px] text-white/30 ml-1.5">
              {Math.round(pos.speed || 0)} km/h
            </span>
          )}
          {isPlaying && (
            <span className="text-[9px] text-emerald-400/60 ml-2 animate-pulse">● Replay x{playSpeed}</span>
          )}
        </div>

        {/* Range label + Activity badge + Snapped indicator */}
        {isSnapped && (
          <span className="flex items-center gap-1 text-[8px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20"
            data-testid="snapped-badge" title="Trajectoire recalée sur route (Snap-to-Road)">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
            Route
          </span>
        )}
        <span className="text-[9px] text-white/20 font-mono">{zoomLabel}</span>
        {currentActivity && (
          <span className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full"
            style={{ backgroundColor: `${ACTIVITY_COLORS[currentActivity.activity]}20`, color: ACTIVITY_COLORS[currentActivity.activity] }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ACTIVITY_COLORS[currentActivity.activity] }} />
            {ACTIVITY_LABELS[currentActivity.activity] || currentActivity.activity}
          </span>
        )}
      </div>

      {/* Timeline bar area */}
      <div className="px-3 pt-2 pb-1 relative">
        {/* Segment bar + cursor */}
        <div
          ref={barRef}
          className="relative h-7 bg-white/5 rounded cursor-pointer group"
          onMouseDown={handleMouseDown}
          onMouseMove={handleBarHover}
          onMouseLeave={() => setHoverRatio(null)}
          data-testid="timeline-bar-interactive"
        >
          {/* Colored segments */}
          {segmentBars.map((seg, i) => (
            <div key={i} className="absolute top-0 bottom-0 transition-opacity hover:opacity-90"
              style={{ left: seg.left, width: seg.width, backgroundColor: seg.color, opacity: 0.7 }}
              title={`${seg.label} - ${seg.timeLabel}`} />
          ))}

          {/* Data range indicator (day view only) */}
          {zoomLevel === ZOOM_DAY && dataRange && (
            <div className="absolute top-0 bottom-0 border-l border-r border-white/20 pointer-events-none"
              style={{
                left: `${((dataRange.start - timeRange.start) / (timeRange.end - timeRange.start)) * 100}%`,
                width: `${((dataRange.end - dataRange.start) / (timeRange.end - timeRange.start)) * 100}%`,
              }} />
          )}

          {/* TL-22: Event markers on timeline */}
          {eventMarkers.map((evt) => (
            <div key={evt.index}
              className="absolute top-0 bottom-0 z-10 flex items-end justify-center cursor-pointer hover:scale-125 transition-transform"
              style={{ left: `${evt.ratio * 100}%`, width: '12px', marginLeft: '-6px' }}
              onClick={(e) => { e.stopPropagation(); setActiveEvent(activeEvent?.index === evt.index ? null : evt); onEventClick?.(evt); }}
              data-testid={`timeline-event-marker-${evt.index}`}
              title={`${evt.style.label} - ${fmtTimeShort(toMs(evt.timestamp))}`}
            >
              <div className="w-2.5 h-2.5 rounded-full border border-white/80 mb-0.5 shadow-sm"
                style={{ backgroundColor: evt.style.color }} />
            </div>
          ))}

          {/* TL-23: Event popup */}
          {activeEvent && (
            <div className="absolute -top-[72px] z-30 pointer-events-auto"
              style={{ left: `${activeEvent.ratio * 100}%`, transform: 'translateX(-50%)' }}>
              <div className="bg-[#1a1a2e] border rounded-lg px-3 py-2 text-[10px] whitespace-nowrap shadow-xl min-w-[160px]"
                style={{ borderColor: activeEvent.style.color + '60' }}
                data-testid="timeline-event-popup">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: activeEvent.style.color }} />
                  <span className="font-medium" style={{ color: activeEvent.style.color }}>{activeEvent.style.label}</span>
                </div>
                <p className="text-white/70 font-mono">{fmtTime(toMs(activeEvent.timestamp))}</p>
                {activeEvent.address && <p className="text-white/50 truncate max-w-[200px]">{activeEvent.address}</p>}
                {activeEvent.note && <p className="text-amber-400/60 italic">"{activeEvent.note}"</p>}
                {activeEvent.arret_motif && <p className="text-amber-400/80">Motif: {activeEvent.arret_motif}</p>}
                <button onClick={(e) => { e.stopPropagation(); setActiveEvent(null); }}
                  className="absolute top-1 right-1.5 text-white/30 hover:text-white text-[10px]">x</button>
              </div>
              <div className="w-0 h-0 mx-auto border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent"
                style={{ borderTopColor: activeEvent.style.color + '60' }} />
            </div>
          )}

          {/* Hover tooltip (TL-27) */}
          {hoverRatio !== null && !isDragging && hoverTimestamp && (
            <div className="absolute -top-10 pointer-events-none z-10" style={{ left: `${hoverRatio * 100}%`, transform: 'translateX(-50%)' }}>
              <div className="bg-[#1a1a2e] border border-white/20 rounded px-2 py-0.5 text-[9px] text-white/70 whitespace-nowrap shadow-lg">
                {fmtTime(hoverTimestamp)}
                {hoverPos && <span className="text-[#C9A24D] ml-1">{Math.round(hoverPos.speed || 0)} km/h</span>}
              </div>
            </div>
          )}

          {/* Cursor */}
          <div className="absolute top-0 bottom-0 pointer-events-none z-20" style={{ left: `${cursorRatio * 100}%` }}>
            <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[7px] border-l-transparent border-r-transparent border-t-[#C9A24D] drop-shadow-sm" />
            <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-0.5 bg-[#C9A24D]" />
          </div>

          {/* Empty state */}
          {!hasData && (
            <div className="absolute inset-0 flex items-center justify-center text-[10px] text-white/20">
              Aucune donnée GPS
            </div>
          )}

          {/* Zoom hint */}
          {zoomLevel < ZOOM_QUARTER && hasData && (
            <div className="absolute inset-0 flex items-center justify-center text-[9px] text-white/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Cliquer pour zoomer
            </div>
          )}
        </div>

        {/* Time axis labels */}
        <div className="relative h-4 mt-0.5">
          {axisLabels.map((item, i) => (
            <span key={i} className="absolute text-[9px] text-white/30 -translate-x-1/2"
              style={{ left: `${item.ratio * 100}%` }}>
              {item.label}
            </span>
          ))}
        </div>

        {/* Legend row */}
        <div className="flex items-center justify-between mt-0.5">
          <div className="flex items-center gap-2 text-[8px] text-white/30">
            {Object.entries(ACTIVITY_LABELS).filter(([k]) => k !== 'STOP').map(([key, label]) => (
              <span key={key} className="flex items-center gap-0.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ACTIVITY_COLORS[key] }} />{label}
              </span>
            ))}
          </div>
          {pos && hasData && (
            <span className="text-[8px] text-white/20 font-mono" data-testid="timeline-coordinates">
              {pos.lat?.toFixed(4)}, {pos.lng?.toFixed(4)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
